import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DATE_SHORT_MMM_DD_YYYY_H_MMA } from 'common/constans/date.constants';
import { ICitationRead } from 'common/services/citation-service/citation-service.types';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import { observer } from 'mobx-react-lite';
import citationService from 'common/services/citation-service/citation-service';
import { useTableContext } from 'common/components/table/table-context';
import eiqConnectStore from 'features/eiq-connect/store/eiq-connect.store';
import { Button, Tag } from 'antd';
import { CitationStatus, citationStatusColorMap, citationStatusMap } from 'common/enums/citation-status.enum';
import dayjs from 'dayjs';
import { useMedia } from 'common/hooks/use-media/use-media.hook';
import { ReactComponent as ViolationSpeedIcon } from 'assets/icons/eiq-connect/violating-speed.svg';
import { ReactComponent as PayFine } from 'assets/icons/eiq-connect/pay-fine.svg';
import EIQImageGallery from 'features/eiq-connect/components/image-gallery/image-gallary';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import './citation-details.scss';
import { EiqDetailsRow } from 'common/components/eiq-details/eiq-details-row';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { getCitationGuestName, getCitationVisitorName } from 'common/helpers/citation.helper';
import { ICheckoutModalProps } from './checkout-modal/checkout-modal.types';
import { CheckoutModal } from './checkout-modal/checkout-modal';
import { notification } from 'common/utils/notification';
import TopHeader from '../../../components/top-header/top-header';
import BottomHeader from '../../../components/bottom-header/bottom-header';
import { visitorTypeMap, visitorTypeMapColorMap } from 'common/constans/visitor.constants';
import { TrafficLogVisitorType } from 'common/constans/common.constants';
import configurationStore from 'common/stores/configuration/configuration.store';
import { ICitationRuleConfiguration } from 'common/services/configuration-service/configuration.service.types';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';

const CitationDetails = observer(() => {
  const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

  const { id } = useParams();
  const navigate = useNavigate();
  const tableContext = useTableContext();
  const selectedRow = tableContext.getSelectedRow() as ICitationRead;
  const [initialValues, setInitialValues] = useState<ICitationRead>();
  const webGuestResidentId = userAuthStore.webGuestResidentId;
  const { isMobile } = useMedia();

  const [clientSecret, setClientSecret] = useState('');
  const [stripePromise, setStipePromise] = useState<Promise<Stripe | null> | null>(null);

  const citationConfiguration = configurationStore.configuration?.citations;

  useEffect(() => {
    if (selectedRow) {
      setInitialValues(selectedRow);
    } else if (webGuestResidentId) {
      citationService
        .getCitationsByResidentId(
          {
            offset: 0,
            limit: 20,
          },
          null,
          '',
          webGuestResidentId,
        )
        .then((result) => {
          tableContext.setSelectedRow(result.items.find((item) => item.id === Number(id)));
        });
    }
  }, [tableContext.getSelectedRow(), eiqConnectStore.residentDetails]);

  const shouldPay = () => {
    return (
      (initialValues?.status === CitationStatus.PayNow ||
        initialValues?.status === CitationStatus.Delinquent ||
        initialValues?.status === CitationStatus.Suspension ||
        initialValues?.status === CitationStatus.PastDue) &&
      !!initialValues?.totalPaymentAmount
    );
  };

  const onPayFineClicked = () => {
    setStipePromise(loadStripe(stripePublicKey!));
    citationService.createPayment(Number(id), webGuestResidentId!).then((result) => {
      setClientSecret(result.clientSecret);
      paymentModal.showModal();
    });
  };

  const paymentModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const data: ICheckoutModalProps = {
        totalPaymentAmount: initialValues!.totalPaymentAmount,
      };

      setModalInitData(data);
    },
    onClose: (isSuccess) => {
      if (isSuccess) {
        notification.success({
          message: 'Thank you, your payment has been received',
          duration: 5,
        });
        navigate('/eiq-connect/citations');
      }
    },
  });

  const onTagClick = () => {
    if (!shouldPay()) {
      return;
    }
    onPayFineClicked();
  };

  const getPhotosUrls = () => {
    if (!initialValues) {
      return [];
    }
    return [...initialValues.photosUrl, ...initialValues.licensePlatePhotosUrl];
  };

  const getLicensePlateState = (citation?: ICitationRead) => {
    if (!citation?.licenseState) {
      return `${citation?.licensePlate}`;
    }
    return `${initialValues?.licensePlate}, ${initialValues?.licenseState}`;
  };

  const getLegalNotes = (rules?: ICitationRuleConfiguration[], sequenceNumber?: number | null): string => {
    if (!rules) {
      return '';
    }

    if (rules.length === 0 || sequenceNumber === null || sequenceNumber === undefined || sequenceNumber < 1) {
      return '';
    }

    const ruleIndex = Math.min(sequenceNumber - 1, rules.length - 1);
    return rules[ruleIndex]?.legalNotes || '';
  };

  const getCitationNotes = () => {
    return (
      <div className="eiq-details-row" style={{ whiteSpace: 'pre-line' }}>
        {getLegalNotes(configurationStore?.configuration?.citations.rules, selectedRow?.sequenceNumber)}
      </div>
    );
  };

  const getTransponderSuspendedText = (days: number) => {
    return `${days} ${days === 1 ? 'day' : 'days'} transponder suspension`;
  };

  const getConsequences = (citation?: ICitationRead) => {
    if (!citation) {
      return '';
    }

    if (citation.status === CitationStatus.Warning) {
      return 'Warning';
    }

    if (citation.totalPaymentAmount === 0) {
      return '$0';
    }

    if (citation.totalPaymentAmount && !citation.suspensionDays) {
      return `$${citation.totalPaymentAmount} Due within 2 weeks`;
    }

    if (citation.suspensionDays !== null && !citation.totalPaymentAmount) {
      return getTransponderSuspendedText(citation.suspensionDays);
    }

    if (citation.totalPaymentAmount && citation.suspensionDays !== null) {
      return `$${citation.totalPaymentAmount} Due within 2 weeks 
${getTransponderSuspendedText(citation.suspensionDays)}`;
    }

    return '';
  };

  return (
    <div className="eiq-guests-page">
      <div className="eiq-header">
        <TopHeader />
        <BottomHeader title="Details" />
      </div>

      <div className="eiq-content">
        <div className="w-100 citation-wrapper">
          <EIQImageGallery items={getPhotosUrls()} />
          {isMobile ? (
            <>
              <EiqDetailsRow title="Citation Number" value={initialValues?.externalId} />
              <div className="eiq-details-row">
                <div className="eiq-details-row__title">Status</div>
                <div className="eiq-details-row-tags">
                  <Tag
                    onClick={onTagClick}
                    className="eiq-tag"
                    color={citationStatusColorMap[initialValues?.status as CitationStatus]}
                    style={{ color: '#292B2E' }}>
                    {citationStatusMap[initialValues?.status as CitationStatus]}
                  </Tag>
                </div>
              </div>
              <EiqDetailsRow title="License Plate, State" value={getLicensePlateState(initialValues)} />
              <EiqDetailsRow title="Type" value={visitorTypeMap[initialValues?.ownerType as TrafficLogVisitorType]} />
              {initialValues?.guestId && <EiqDetailsRow title="Name" value={getCitationGuestName(initialValues)} />}
            </>
          ) : (
            <>
              <div className="details">
                <div className="title guestName">
                  {getCitationVisitorName(initialValues)}
                  <Tag
                    onClick={onTagClick}
                    className="eiq-tag"
                    color={citationStatusColorMap[initialValues?.status as CitationStatus]}
                    style={{ color: '#292B2E' }}>
                    {citationStatusMap[initialValues?.status as CitationStatus]}
                  </Tag>
                  {initialValues?.guestId && (
                    <Tag
                      className="eiq-tag"
                      color={visitorTypeMapColorMap[initialValues?.ownerType as TrafficLogVisitorType]}
                      style={{ color: '#292B2E' }}>
                      {visitorTypeMap[initialValues?.ownerType as TrafficLogVisitorType]}
                    </Tag>
                  )}
                </div>
                <div className="text">{getLicensePlateState(initialValues)}</div>
              </div>
              <EiqDetailsRow title="Citation Number" value={initialValues?.externalId} />
            </>
          )}
          <EiqDetailsRow title="Speed Limit" value={`${initialValues?.speedLimit} mph`} />
          <div className="eiq-details-row violating-speed-row">
            <div className="eiq-details-row__title">Recorded Speed</div>
            <div className="text violating-speed">
              <ViolationSpeedIcon />
              <span>{initialValues?.violatingSpeed} mph</span>
            </div>
          </div>
          <EiqDetailsRow title="Date & Time" value={StrToDateTimeLocal(initialValues?.occurredAt)?.format(DATE_SHORT_MMM_DD_YYYY_H_MMA)} />
          <EiqDetailsRow title="Citation Count" value={initialValues?.sequenceNumber} />
          <EiqDetailsRow title="Penalty" value={getConsequences(initialValues)} className="fine-amount" />
          {isMobile && getCitationNotes()}
          {!!initialValues?.totalPaymentAmount && !!citationConfiguration?.isAutoIssueInvoice && (
            <div className="fine">
              <Button type="primary" icon={<PayFine />} disabled={!shouldPay()} onClick={onPayFineClicked}>
                Pay Now
              </Button>
            </div>
          )}
        </div>
        {!isMobile && getCitationNotes()}
      </div>

      {clientSecret && stripePromise && shouldPay() && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutModal
            isEdit={false}
            title="Payment"
            isOpen={paymentModal.isOpenModal}
            onClose={paymentModal.hideModal}
            initData={paymentModal.initData}
          />
        </Elements>
      )}
    </div>
  );
});

export default CitationDetails;
